import React, { useRef, useEffect } from 'react';
import { WabisabiIcon } from '@/components/common/WabisabiIcon';

interface CarouselItem {
  image: string;
  subtitle: string;
}

const carouselItems: CarouselItem[] = [
  {
    image: '/images/img4.jpg',
    subtitle: "The group chat finally made it to dinner. Wabisabi helps you remember the laughter, not just the logistics.",
  },
  {
    image: '/images/img7.jpg',
    subtitle: "A splash of color, a burst of laughter, and hands full of love.",
  },
  {
    image: '/images/img6.jpg',
    subtitle: "Some friendships don't need constant check-ins. Just shared laughter and a drink that says 'I missed you.'",
  },
  {
    image: '/images/img2.jpg',
    subtitle: "A shared board. A quiet bond. Wabisabi helps you remember the small traditions that build connection over time.",
  }
];

export const AutoScrollCarousel: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();

  const getIconPosition = (index: number) => {
    const positions = ['self-start mt-20', 'self-center', 'self-end mb-20', 'self-center'];
    return positions[index] || positions[index % 3];
  };

  // Create array with items and icons
  const itemsWithIcons = [];
  for (let i = 0; i < carouselItems.length; i++) {
    itemsWithIcons.push({ type: 'item', data: carouselItems[i], index: i });
    itemsWithIcons.push({ type: 'icon', index: i });
  }

  // Double the array for seamless infinite scroll
  const allItems = [...itemsWithIcons, ...itemsWithIcons];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollPos = 0;
    const scrollSpeed = 0.5; // Pixels per frame

    const scroll = () => {
      if (!scrollContainer) return;

      scrollPos += scrollSpeed;

      // Get the width of one complete set of items
      const totalWidth = scrollContainer.scrollWidth / 2;

      // Reset scroll position when we've scrolled past the first set
      if (scrollPos >= totalWidth) {
        scrollPos = 0;
      }

      scrollContainer.style.transform = `translateX(-${scrollPos}px)`;
      animationRef.current = requestAnimationFrame(scroll);
    };

    animationRef.current = requestAnimationFrame(scroll);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="relative w-full overflow-hidden">
      <div ref={scrollRef} className="flex" style={{ width: 'fit-content' }}>
        {allItems.map((item, idx) => (
          <React.Fragment key={idx}>
            {item.type === 'item' ? (
              <div
                className={`flex-shrink-0 ${item.index % 2 === 1 ? 'mt-[50px]' : ''}`}
              >
                <div className="w-[308px] md:w-[534px]">
                  <img
                    src={item.data.image}
                    alt={item.data.subtitle}
                    className="w-full h-[253px] md:h-[440px] object-cover rounded-[8px] border border-[#2C2C2C]"
                  />
                  <p className="mt-4 text-center text-xs md:text-base text-gray-700 font-euclid max-w-[308px] md:max-w-[534px]">
                    {item.data.subtitle}
                  </p>
                </div>
              </div>
            ) : (
              <div className={`flex-shrink-0 flex items-center ${getIconPosition(item.index)}`}>
                <WabisabiIcon 
                  size={70} 
                  className={item.index % 4 === 0 ? 'animate-float' : 
                            item.index % 4 === 1 ? 'animate-float-delay-1' : 
                            item.index % 4 === 2 ? 'animate-float-delay-2' : 
                            'animate-float-delay-3'} 
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
